import { 
    Box, 
    Button, 
    Dialog, 
    DialogActions, 
    DialogContent, 
    DialogTitle, 
    IconButton,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    Typography,
    Chip,
    Switch,
    FormControlLabel
} from '@mui/material';
import React, { useState } from 'react';
import { RiBracesLine, RiArrowDownSLine } from '@remixicon/react';
import CopyToClipboard from 'react-copy-to-clipboard';
import useApi from '../../../hooks/useGaneshApi';

interface JsonDisplayModalWithFetchProps {
    fetchUrl: string;
    title?: string;
    buttonLabel?: string;
    iconOnly?: boolean;
    disabled?: boolean;
    fetchParams?: Record<string, any>;
    enableCollapse?: boolean;
}

const JsonDisplayModalWithFetch: React.FC<JsonDisplayModalWithFetchProps> = ({
    fetchUrl,
    title = "JSON Data",
    buttonLabel = "View JSON",
    iconOnly = true,
    disabled = false,
    fetchParams = {},
    enableCollapse = true
}) => {
    const [modalOpen, setModalOpen] = useState<boolean>(false);
    const [jsonData, setJsonData] = useState<any>(null);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [useCollapsibleView, setUseCollapsibleView] = useState<boolean>(enableCollapse);
    const { apiRequest } = useApi();

    const handleOpenModal = async () => {
        setModalOpen(true);
        setIsLoading(true);
        
        try {
            const response = await apiRequest(fetchUrl, {
                method: 'GET',
                ...fetchParams
            });
            
            // Handle both old and new response formats
            const data = (response as any).data?.data || (response as any).data || response;
            setJsonData(data);
        } catch (error) {
            console.error('Failed to fetch JSON data:', error);
            setJsonData({ error: 'Failed to load data' });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCloseModal = () => {
        setModalOpen(false);
        setJsonData(null);
    };

    const formatJsonData = (data: any) => {
        if (typeof data === 'string') {
            try {
                return JSON.parse(data);
            } catch {
                return data;
            }
        }
        return data;
    };

    const getValueType = (value: any): string => {
        if (value === null) return 'null';
        if (Array.isArray(value)) return 'array';
        return typeof value;
    };

    const getValuePreview = (value: any): string => {
        const type = getValueType(value);
        switch (type) {
            case 'object':
                const keyCount = Object.keys(value).length;
                return `{${keyCount} key${keyCount !== 1 ? 's' : ''}}`;
            case 'array':
                const itemCount = value.length;
                return `[${itemCount} item${itemCount !== 1 ? 's' : ''}]`;
            case 'string':
                return value.length > 50 ? `"${value.substring(0, 50)}..."` : `"${value}"`;
            case 'number':
                // Format large numbers with commas
                return typeof value === 'number' && value > 999 ? 
                    value.toLocaleString() : String(value);
            case 'boolean':
            case 'null':
                return String(value);
            default:
                return String(value);
        }
    };

    const renderArrayAsTable = (array: any[], keyName: string = 'key', valueName: string = 'value', maxDisplay?: number) => {
        if (!Array.isArray(array) || array.length === 0) return null;
        
        // Check if array contains objects with consistent structure
        const firstItem = array[0];
        if (typeof firstItem !== 'object' || firstItem === null) return null;
        
        const keys = Object.keys(firstItem);
        
        // Check if this is a fill_rate array (supports both old and new summary schema)
        const isFillRateArray = keys.includes('fill_rate') || 
                               (keys.includes('attribute') && keys.includes('label')) ||
                               (keys.includes('column') && keys.includes('fill'));
        
        // For fill_rates, use specific column order based on schema version
        let orderedKeys: string[];
        if (isFillRateArray) {
            if (keys.includes('column') && keys.includes('fill')) {
                // Old schema: column, fill
                orderedKeys = ['column', 'fill'];
            } else if (keys.includes('attribute') && keys.includes('label') && keys.includes('fill_rate')) {
                // New schema: attribute, label, fill_rate
                orderedKeys = ['attribute', 'label', 'fill_rate'];
            } else {
                // Fallback to original keys
                orderedKeys = keys;
            }
        } else {
            orderedKeys = keys;
        }
        
        // Determine max display based on content type
        let displayLimit = maxDisplay || 10;
        if (!maxDisplay) {
            // For fill_rates, show all items
            // For top_values, show limited items
            if (isFillRateArray) {
                displayLimit = array.length; // Show all fill rates
            } else {
                displayLimit = 10; // Default for top values
            }
        }
        
        return (
            <Box sx={{ mt: 1 }}>
                {/* Header */}
                <Box sx={{ 
                    display: 'grid', 
                    gridTemplateColumns: `repeat(${orderedKeys.length}, 1fr)`, 
                    gap: 1,
                    mb: 1,
                    p: 1,
                    backgroundColor: '#f0f0f0',
                    borderRadius: 1,
                    fontWeight: 'bold',
                    position: 'sticky',
                    top: 0,
                    zIndex: 1
                }}>
                    {orderedKeys.map(key => (
                        <Typography key={key} variant="body2" sx={{ fontSize: '0.8rem', textTransform: 'capitalize' }}>
                            {key.replace(/_/g, ' ')}
                        </Typography>
                    ))}
                </Box>
                
                {/* Scrollable content area for large datasets */}
                <Box sx={{ 
                    maxHeight: isFillRateArray ? '400px' : '300px', 
                    overflowY: 'auto',
                    border: '1px solid #e0e0e0',
                    borderRadius: 1
                }}>
                    {array.slice(0, displayLimit).map((item, index) => (
                        <Box key={index} sx={{ 
                            display: 'grid', 
                            gridTemplateColumns: `repeat(${orderedKeys.length}, 1fr)`, 
                            gap: 1,
                            p: 1,
                            borderBottom: index < displayLimit - 1 ? '1px solid #e0e0e0' : 'none',
                            '&:hover': { backgroundColor: '#f9f9f9' },
                            // Highlight high fill rates in green
                            backgroundColor: isFillRateArray && item.fill_rate >= 90 ? '#e8f5e8' : 
                                           isFillRateArray && item.fill_rate <= 10 ? '#ffe8e8' : 'inherit'
                        }}>
                            {orderedKeys.map(key => (
                                <Typography key={key} variant="body2" sx={{ 
                                    fontSize: '0.8rem', 
                                    fontFamily: typeof item[key] === 'number' ? 'monospace' : 'inherit',
                                    fontWeight: typeof item[key] === 'number' ? 'bold' : 'normal'
                                }}>
                                    {typeof item[key] === 'number' && item[key] > 999 ? 
                                        item[key].toLocaleString() : 
                                        typeof item[key] === 'number' && isFillRateArray && key === 'fill_rate' ?
                                        `${item[key]}%` : String(item[key])}
                                </Typography>
                            ))}
                        </Box>
                    ))}
                </Box>
                
                {array.length > displayLimit && (
                    <Typography variant="body2" sx={{ 
                        textAlign: 'center', 
                        color: 'text.secondary', 
                        fontStyle: 'italic',
                        mt: 1,
                        p: 1,
                        backgroundColor: '#f8f9fa',
                        borderRadius: 1
                    }}>
                        Showing {displayLimit} of {array.length} items
                    </Typography>
                )}
            </Box>
        );
    };

    const renderJsonValue = (value: any, depth: number = 0): React.ReactNode => {
        const type = getValueType(value);
        
        if (type === 'object' && value !== null) {
            return (
                <Box sx={{ ml: depth * 2 }}>
                    {Object.entries(value).map(([key, val], index) => (
                        <Box key={key} sx={{ mb: 1.5 }}>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', color: 'primary.main' }}>
                                {key.replace(/_/g, ' ')}:
                            </Typography>
                            {' '}
                            {getValueType(val) === 'object' || getValueType(val) === 'array' ? (
                                <Box sx={{ mt: 0.5 }}>
                                    {renderJsonValue(val, depth + 1)}
                                </Box>
                            ) : (
                                <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
                                    {getValuePreview(val)}
                                </Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            );
        }
        
        if (type === 'array') {
            // Check if it's an array of objects that can be displayed as a table
            if (value.length > 0 && typeof value[0] === 'object' && value[0] !== null) {
                const tableView = renderArrayAsTable(value);
                if (tableView) return tableView;
            }
            
            // Fallback to regular array rendering
            const maxItems = 5;
            return (
                <Box sx={{ ml: depth * 2 }}>
                    {value.slice(0, maxItems).map((item: any, index: number) => (
                        <Box key={index} sx={{ mb: 1 }}>
                            <Typography variant="body2" component="span" sx={{ fontWeight: 'bold', color: 'secondary.main' }}>
                                [{index}]:
                            </Typography>
                            {' '}
                            {getValueType(item) === 'object' || getValueType(item) === 'array' ? (
                                <Box sx={{ mt: 0.5 }}>
                                    {renderJsonValue(item, depth + 1)}
                                </Box>
                            ) : (
                                <Typography variant="body2" component="span" sx={{ fontFamily: 'monospace' }}>
                                    {getValuePreview(item)}
                                </Typography>
                            )}
                        </Box>
                    ))}
                    {value.length > maxItems && (
                        <Typography variant="body2" sx={{ 
                            color: 'text.secondary', 
                            fontStyle: 'italic',
                            ml: 2 
                        }}>
                            ... and {value.length - maxItems} more items
                        </Typography>
                    )}
                </Box>
            );
        }
        
        return (
            <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                {getValuePreview(value)}
            </Typography>
        );
    };

    const getSectionDescription = (key: string, value: any): string => {
        const type = getValueType(value);
        
        // Special descriptions for export summary sections
        const descriptions: Record<string, string> = {
            'fill_rates': `Data quality: Fill rates for all ${Array.isArray(value) ? value.length : ''} columns`,
            'placeset_stats': 'Overall statistics about the places in this export',
            'top_values': 'Most common values for key categorical fields',
            'open_closed_stats': 'Statistics about business open/closed status',
            'brands': `Top ${Array.isArray(value) ? Math.min(10, value.length) : ''} branded locations by count`,
            'business_categories': `Top ${Array.isArray(value) ? Math.min(10, value.length) : ''} business types`,
            'categories': `Top ${Array.isArray(value) ? Math.min(10, value.length) : ''} main categories`,
            'cities': `Top ${Array.isArray(value) ? Math.min(10, value.length) : ''} cities by location count`,
            'states': `Top ${Array.isArray(value) ? Math.min(10, value.length) : ''} states by location count`,
            'naics_codes': `Top ${Array.isArray(value) ? Math.min(10, value.length) : ''} NAICS industry codes`,
            'postal_codes': `Top ${Array.isArray(value) ? Math.min(10, value.length) : ''} postal codes by density`
        };
        
        if (descriptions[key]) {
            return descriptions[key];
        }
        
        // Fallback to generic descriptions
        if (type === 'array' && value.length > 0) {
            return `${value.length} item${value.length !== 1 ? 's' : ''}`;
        }
        if (type === 'object') {
            const keyCount = Object.keys(value).length;
            return `${keyCount} propert${keyCount !== 1 ? 'ies' : 'y'}`;
        }
        
        return getValuePreview(value);
    };

    const renderCollapsibleJson = (data: any) => {
        if (!data || typeof data !== 'object') {
            return renderJsonValue(data);
        }

        // For export summaries, focus on specific sections only
        let relevantData = data;
        
        if (data.summary && typeof data.summary === 'object') {
            // If there's a summary object, filter to show only the 4 key sections
            const summaryData = data.summary;
            relevantData = {};
            
            // 1. Data quality metrics - but only fill_rates
            // First check for data_quality_metrics.fill_rates (backward compatibility)
            if (summaryData.data_quality_metrics?.fill_rates) {
                // Old schema: fill_rate already comes as percentage, just limit to 2 decimal places
                console.log('Using data_quality_metrics.fill_rates:', summaryData.data_quality_metrics.fill_rates);
                relevantData.fill_rates = summaryData.data_quality_metrics.fill_rates.map((item: any) => ({
                    ...item,
                    fill_rate: item.fill_rate !== null ? parseFloat(item.fill_rate.toFixed(2)) : null
                }));
            } else if (summaryData.columns) {
                // Auto-detect schema version based on data structure
                if (Array.isArray(summaryData.columns) && summaryData.columns.length > 0) {
                    const firstColumn = summaryData.columns[0];
                    
                    if (typeof firstColumn === 'string') {
                        // Old schema: array of column names (strings)
                        // Convert to format with attribute, label and fill_rate
                        console.log('Converting columns array to fill_rates structure:', summaryData.columns);
                        relevantData.fill_rates = summaryData.columns.map((columnName: string) => ({
                            attribute: columnName, // Keep original column name as attribute
                            label: columnName, // Keep original column name as label
                            fill_rate: null // No fill rate data available in old schema
                        }));
                    } else if (typeof firstColumn === 'object' && firstColumn !== null) {
                        // New schema: array of objects - convert fill_rate from decimal to percentage with 2 decimal places
                        relevantData.fill_rates = summaryData.columns.map((item: any) => ({
                            ...item,
                            fill_rate: item.fill_rate !== null ? parseFloat((item.fill_rate * 100).toFixed(2)) : null
                        }));
                    }
                }
            }
            
            // 2. Open Closed Stats
            if (summaryData.open_closed_stats) {
                relevantData.open_closed_stats = summaryData.open_closed_stats;
            }
            
            // 3. Placeset Stats
            if (summaryData.placeset_stats) {
                relevantData.placeset_stats = summaryData.placeset_stats;
            }
            
            // 4. Top Values
            if (summaryData.top_values) {
                relevantData.top_values = summaryData.top_values;
            }
        } else {
            // Filter out metadata fields for other JSON structures
            const metadataFields = [
                'export_id', 'column_count', 'row_count', 'file_size_kb', 
                'file_type', 'export_created_at', 'file_created_at'
            ];
            
            relevantData = Object.fromEntries(
                Object.entries(data).filter(([key]) => !metadataFields.includes(key))
            );
        }

        return (
            <Box>
                {Object.entries(relevantData).map(([key, value]) => (
                    <Accordion key={key} sx={{ mb: 1 }}>
                        <AccordionSummary 
                            expandIcon={<RiArrowDownSLine />}
                            sx={{ 
                                backgroundColor: '#f8f9fa',
                                '&:hover': { backgroundColor: '#e9ecef' }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                                <Typography variant="h6" sx={{ fontWeight: 'bold', textTransform: 'capitalize' }}>
                                    {key.replace(/_/g, ' ')}
                                </Typography>
                                <Chip 
                                    label={getValueType(value)} 
                                    size="small" 
                                    variant="outlined"
                                    color={getValueType(value) === 'object' ? 'primary' : 'secondary'}
                                />
                                <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                                    {getSectionDescription(key, value)}
                                </Typography>
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ backgroundColor: '#ffffff' }}>
                            <Box sx={{
                                backgroundColor: '#f5f5f5',
                                padding: 1,
                                borderRadius: 1,
                                border: '1px solid #ddd',
                            }}>
                                {getValueType(value) === 'object' || getValueType(value) === 'array' ? 
                                    renderJsonValue(value) : 
                                    <pre style={{ margin: 0, whiteSpace: 'pre-wrap', wordWrap: 'break-word' }}>
                                        {JSON.stringify(value, null, 2)}
                                    </pre>
                                }
                            </Box>
                        </AccordionDetails>
                    </Accordion>
                ))}
            </Box>
        );
    };

    const formattedData = formatJsonData(jsonData);
    const jsonString = JSON.stringify(formattedData, null, 2);

    return (
        <>
            {iconOnly ? (
                <IconButton onClick={handleOpenModal} disabled={disabled}>
                    <RiBracesLine />
                </IconButton>
            ) : (
                <Button onClick={handleOpenModal} disabled={disabled}>
                    {buttonLabel}
                </Button>
            )}

            <Dialog 
                open={modalOpen} 
                onClose={handleCloseModal} 
                maxWidth="lg" 
                fullWidth
                PaperProps={{
                    sx: {
                        height: '95vh',
                        maxHeight: '95vh',
                        margin: '2.5vh auto'
                    }
                }}
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">{title}</Typography>
                        {enableCollapse && (
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={useCollapsibleView}
                                        onChange={(e) => setUseCollapsibleView(e.target.checked)}
                                        size="small"
                                    />
                                }
                                label={useCollapsibleView ? "Summary view" : "Raw JSON"}
                                sx={{ ml: 2 }}
                            />
                        )}
                    </Box>
                </DialogTitle>
                <DialogContent 
                    sx={{ 
                        height: 'calc(100% - 140px)', // Account for title and actions
                        display: 'flex',
                        flexDirection: 'column',
                        padding: 2
                    }}
                >
                    {isLoading ? (
                        <Box sx={{ padding: 2, display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
                            <Typography>Loading data...</Typography>
                        </Box>
                    ) : (
                        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                            <Box display="flex" justifyContent="flex-end" alignItems="center" mb={2}>
                                <CopyToClipboard text={jsonString}>
                                    <Button variant="contained" color="primary">
                                        Copy to Clipboard
                                    </Button>
                                </CopyToClipboard>
                            </Box>
                            
                            {useCollapsibleView && enableCollapse ? (
                                <Box sx={{ flexGrow: 1, overflowY: 'auto', pr: 1 }}>
                                    {renderCollapsibleJson(formattedData)}
                                </Box>
                            ) : (
                                <Box sx={{
                                    backgroundColor: '#f5f5f5',
                                    padding: 2,
                                    borderRadius: 2,
                                    flexGrow: 1,
                                    overflowY: 'auto',
                                    border: '1px solid #ddd'
                                }}>
                                    <pre style={{ whiteSpace: 'pre-wrap', wordWrap: 'break-word', margin: 0 }}>
                                        {jsonString}
                                    </pre>
                                </Box>
                            )}
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseModal} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default JsonDisplayModalWithFetch;
